import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Package,
  Truck,
  Warehouse,
  ClipboardList,
  AlertCircle,
  Clock3,
  RefreshCcw,
  ChevronRight,
  Settings,
  Wrench,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { BASE_URL } from "../../config";
import "./home.css";

// ---------------------------------------------------------------------------
// Config — uses your existing BASE_URL from ../../config. Adjust the import
// path above if this file lives somewhere other than two levels deep from
// wherever your config module sits.
// ---------------------------------------------------------------------------
const API_BASE_URL = BASE_URL || "";

if (!API_BASE_URL) {
  // eslint-disable-next-line no-console
  console.warn(
    "[Home] API_BASE_URL is empty — requests will go to a relative " +
      "path on the frontend's own origin, which is a common cause of every call 404ing at once."
  );
}

const AUTH_STORAGE_KEY = "NagpurProperties";

const http = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
});

http.interceptors.request.use((config) => {
  const stored = localStorage.getItem(AUTH_STORAGE_KEY);
  if (stored) {
    try {
      const { token } = JSON.parse(stored);
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch {
      // eslint-disable-next-line no-console
      console.warn("[Home] Couldn't parse auth data from localStorage.");
    }
  }
  return config;
});

http.interceptors.response.use(
  (res) => res,
  (err) => {
    if (axios.isCancel(err)) return Promise.reject(err);
    // eslint-disable-next-line no-console
    console.error(
      `[Home] ${err.config?.method?.toUpperCase()} ${err.config?.url} failed:`,
      err.response?.status ?? "no response (network/CORS)",
      err.response?.data ?? err.message
    );
    return Promise.reject(err);
  }
);

// ---------------------------------------------------------------------------
// Current user / role — read once from the same localStorage blob the auth
// interceptor above uses. Engineers only ever see their own requests and
// never see the Machine panel; everything else (admin, etc.) keeps the
// existing "review queue for everyone" behavior.
// ---------------------------------------------------------------------------
function getCurrentUser() {
  try {
    const stored = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!stored) return { id: null, isEngineer: false };
    const parsed = JSON.parse(stored);
    const roles = Array.isArray(parsed?.role) ? parsed.role : [];
    const isEngineer = roles.some(
      (r) => (r?.roleName || "").toLowerCase() === "engineer"
    );
    return { id: parsed?.id ?? null, isEngineer };
  } catch {
    return { id: null, isEngineer: false };
  }
}

// Leave requests are scoped to a month/year — default to the current month.
function currentLeaveParams() {
  const now = new Date();
  return { month: now.getMonth() + 1, year: now.getFullYear() };
}

const ENDPOINTS = {
  vendors: `/showAllvendor`,
  dailyMaterial: (vendorId) => `/DailyMaterial/${vendorId}/site-wise-summary`,
  stockTransfer: `/StockTransfer`,
  // ── admin / review-queue endpoints (unscoped) ──
  advanceRequests: `/pending/advance-request?page=0&size=10`,
  welfareRequests: `/welfare/money-withdrwal/requests?page=0&size=10`,
  leaveRequests: () => {
    const { month, year } = currentLeaveParams();
    return `/all/leave?page=0&size=10&month=${month}&year=${year}`;
  },
  // ── engineer-scoped endpoints (own requests, all statuses) ──
  advanceRequestsEngineer: (engineerId) =>
    `/engineer/${engineerId}/advance-request?page=0&size=10`,
  welfareRequestsEngineer: (engineerId) =>
    `/welfare/engineer/${engineerId}/money-withdrwal/request?page=0&size=10`,
  leaveRequestsEngineer: (engineerId) =>
    `/engineer/${engineerId}/leave?page=0&size=10`,
  // ── machine-scoped endpoints (admin only — never fetched for engineers) ──
  machines: `/machines`,
  machineFuel: (machineId) => `/machines/machine/${machineId}`,
  machineInsurance: (machineId) => `/insurance/by-machine/${machineId}`,
  machineEmi: (machineId) => `/emi/machine/${machineId}`,
  machineMaintenance: (machineId) => `/maintenance/by-machine/${machineId}`,
};

// Where each request type sends the engineer when they click a row/vendor.
const ROUTES = {
  vendor: (id) => `/material/${id}`,
  advance: () => `/engineerPayment`,
  welfare: () => `/engineerPayment`,
  leave: () => `/engineerLeave`,
  machine: (id) => `/view-machine/${id}`,
};

const STATUS_COLORS = {
  today: "#2563eb",
  pending: "#d97706",
  approved: "#16a34a",
  rejected: "#dc2626",
};

const PIE_COLORS = ["#2563eb", "#d97706", "#16a34a", "#9333ea", "#dc2626"];

// Cache successful loads in memory for the life of the tab, so revisiting
// this page renders instantly while a fresh copy loads in the background
// (stale-while-revalidate). Reset on hard refresh, which is fine.
let memoryCache = null;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function isSameDay(dateA, dateB) {
  return (
    dateA.getFullYear() === dateB.getFullYear() &&
    dateA.getMonth() === dateB.getMonth() &&
    dateA.getDate() === dateB.getDate()
  );
}

// Every likely field name a backend endpoint might use for "when this
// request was made". This is the actual fix for the today/pending bug:
// previously only createdAt/requestedDate were checked, so any request
// using a different field name (requestDate, date, appliedDate, etc.)
// silently failed date parsing, always came back "not today", and fell
// into Pending even when it was submitted moments ago.
//
// Leave gets its own, narrower list on purpose: a leave record carries two
// unrelated dates — createdAt/createdDate (when the request was submitted)
// and fromDate/leaveDate/startDate (the leave period itself). Today/Pending
// must be judged on the submission date only, never the leave period date,
// or a leave applied for last week that starts today would be wrongly
// marked "Today", and one submitted today for a future date would be
// wrongly marked "Pending".
const DATE_FIELDS_BY_TYPE = {
  leave: ["createdAt", "createdDate"],
  default: [
    "createdAt",
    "requestedDate",
    "requestDate",
    "date",
    "appliedDate",
    "applicationDate",
    "createdDate",
  ],
};

function getRequestDate(request, type) {
  const fields = DATE_FIELDS_BY_TYPE[type] || DATE_FIELDS_BY_TYPE.default;
  for (const field of fields) {
    const raw = request[field];
    if (!raw) continue;
    const d = new Date(raw);
    if (!Number.isNaN(d.getTime())) return d;
  }
  return null;
}

function normalizedStatus(request) {
  const status = (request.status || "").toLowerCase();
  if (["approved", "accepted"].includes(status)) return "approved";
  if (["rejected", "declined", "denied"].includes(status)) return "rejected";
  if (status) return status;
  return request.reviewed === true ? "reviewed" : "pending";
}

function isReviewed(request) {
  const status = (request.status || "").toLowerCase();
  return request.reviewed === true || ["approved", "rejected", "reviewed"].includes(status);
}

// Parses each request's date exactly once and tags it, so "today" and
// "pending" are computed from the same value and can never disagree.
// A request with no parseable date at all is treated as pending (safer
// to surface it for review than to silently drop it).
function classifyRequests(requests, type) {
  const today = [];
  const pending = [];
  const now = new Date();

  for (const r of requests) {
    const parsedDate = getRequestDate(r, type);
    const isTodayItem = parsedDate ? isSameDay(parsedDate, now) : false;
    const tagged = { ...r, _type: type, _date: parsedDate };

    if (isTodayItem) {
      today.push(tagged);
    } else if (!isReviewed(r)) {
      pending.push(tagged);
    }
  }

  return { today, pending };
}

// For the engineer's own view: no today/pending split, just every request
// tagged with its type and status so it can be listed and sorted.
function tagAllRequests(requests, type) {
  return requests.map((r) => ({
    ...r,
    _type: type,
    _date: getRequestDate(r, type),
    _status: normalizedStatus(r),
  }));
}

function formatCurrency(amount) {
  if (amount == null) return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatDate(date) {
  if (!date) return "—";
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
}

// Different endpoints on your backend may wrap their array differently —
// { content: [...] } (Spring pagination), { data: [...] }, { items: [...] },
// a bare array, or something else entirely. Normalize all of that here so a
// shape mismatch never crashes a .forEach/.map downstream.
function toArray(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.content)) return payload.content;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
}

const EMPTY_STATE = {
  errors: {},
  vendors: [],
  selectedVendorId: null,
  dailyMaterial: [],
  stockTransfers: [],
  advanceRequests: [],
  welfareRequests: [],
  leaveRequests: [],
  // ── machine section (admin only) ──
  machines: [],
  selectedMachineId: null,
  machineFuelRecords: [],
  machineInsuranceRecords: [],
  machineEmiRecords: [],
  machineMaintenanceRecords: [],
};

// ---------------------------------------------------------------------------
// Data hook
// ---------------------------------------------------------------------------
function useOperationsData() {
  const { id: engineerId, isEngineer } = useMemo(() => getCurrentUser(), []);

  const [state, setState] = useState(() =>
    memoryCache
      ? { ...memoryCache, loading: false, materialLoading: false, machineDataLoading: false }
      : { ...EMPTY_STATE, loading: true, materialLoading: false, machineDataLoading: false }
  );
  const controllerRef = useRef(null);
  const machineControllerRef = useRef(null);

  const loadMaterial = useCallback(async (vendorId, signal) => {
    setState((s) => ({ ...s, materialLoading: true }));
    try {
      const res = await http.get(ENDPOINTS.dailyMaterial(vendorId), { signal });
      const dailyMaterial = toArray(res.data);
      setState((s) => {
        const next = { ...s, dailyMaterial, materialLoading: false };
        memoryCache = next;
        return next;
      });
    } catch (err) {
      if (axios.isCancel(err) || err.name === "CanceledError") return;
      setState((s) => ({
        ...s,
        materialLoading: false,
        errors: { ...s.errors, dailyMaterial: err?.message || "Failed to load daily material" },
      }));
    }
  }, []);

  // Fetches the four machine-scoped panels (fuel, insurance, EMI,
  // maintenance) for one machine id. Uses allSettled so one endpoint
  // failing (e.g. a machine with no insurance yet) doesn't blank the
  // whole panel — each section just reports its own error.
  // Admin-only: never called for the Engineer role.
  const loadMachineData = useCallback(async (machineId, signal) => {
    setState((s) => ({ ...s, machineDataLoading: true }));
    try {
      const [fuelR, insuranceR, emiR, maintenanceR] = await Promise.allSettled([
        http.get(ENDPOINTS.machineFuel(machineId), { signal }),
        http.get(ENDPOINTS.machineInsurance(machineId), { signal }),
        http.get(ENDPOINTS.machineEmi(machineId), { signal }),
        http.get(ENDPOINTS.machineMaintenance(machineId), { signal }),
      ]);

      if (signal.aborted) return;

      const errs = {};
      const machineFuelRecords =
        fuelR.status === "fulfilled"
          ? toArray(fuelR.value.data)
          : ((errs.machineFuel = fuelR.reason?.message || "Failed to load fuel records"), []);
      const machineInsuranceRecords =
        insuranceR.status === "fulfilled"
          ? toArray(insuranceR.value.data)
          : ((errs.machineInsurance = insuranceR.reason?.message || "Failed to load insurance records"), []);
      const machineEmiRecords =
        emiR.status === "fulfilled"
          ? toArray(emiR.value.data)
          : ((errs.machineEmi = emiR.reason?.message || "Failed to load EMI records"), []);
      const machineMaintenanceRecords =
        maintenanceR.status === "fulfilled"
          ? toArray(maintenanceR.value.data)
          : ((errs.machineMaintenance = maintenanceR.reason?.message || "Failed to load maintenance records"), []);

      setState((s) => {
        const next = {
          ...s,
          machineFuelRecords,
          machineInsuranceRecords,
          machineEmiRecords,
          machineMaintenanceRecords,
          machineDataLoading: false,
          errors: { ...s.errors, ...errs },
        };
        memoryCache = next;
        return next;
      });
    } catch (err) {
      if (axios.isCancel(err) || err.name === "CanceledError") return;
      setState((s) => ({ ...s, machineDataLoading: false }));
    }
  }, []);

  const load = useCallback(
    async (signal) => {
      // Only show the full-page loader when we have nothing cached to show.
      setState((s) => ({ ...s, loading: !memoryCache, errors: {} }));

      // Engineers get their own scoped advance/welfare/leave endpoints and
      // never fetch the vendors-machine list at all; admins keep the
      // original unscoped "review queue" endpoints plus machines.
      const advanceUrl = isEngineer
        ? ENDPOINTS.advanceRequestsEngineer(engineerId)
        : ENDPOINTS.advanceRequests;
      const welfareUrl = isEngineer
        ? ENDPOINTS.welfareRequestsEngineer(engineerId)
        : ENDPOINTS.welfareRequests;
      const leaveUrl = isEngineer
        ? ENDPOINTS.leaveRequestsEngineer(engineerId)
        : ENDPOINTS.leaveRequests();

      const requests = [
        http.get(ENDPOINTS.vendors, { signal }),
        http.get(ENDPOINTS.stockTransfer, { signal }),
        http.get(advanceUrl, { signal }),
        http.get(welfareUrl, { signal }),
        http.get(leaveUrl, { signal }),
      ];
      if (!isEngineer) {
        requests.push(http.get(ENDPOINTS.machines, { signal }));
      }

      const results = await Promise.allSettled(requests);
      const [vendorsR, stockR, advanceR, welfareR, leaveR, machinesR] = results;

      if (signal.aborted) return;

      const errors = {};
      const vendors =
        vendorsR.status === "fulfilled"
          ? toArray(vendorsR.value.data)
          : ((errors.vendors = vendorsR.reason?.message || "Failed to load vendors"), []);

      const stockTransfers =
        stockR.status === "fulfilled"
          ? toArray(stockR.value.data)
          : ((errors.stock = stockR.reason?.message || "Failed to load stock transfers"), []);

      const advanceRequests =
        advanceR.status === "fulfilled"
          ? toArray(advanceR.value.data)
          : ((errors.advance = advanceR.reason?.message || "Failed to load advance requests"), []);

      const welfareRequests =
        welfareR.status === "fulfilled"
          ? toArray(welfareR.value.data)
          : ((errors.welfare = welfareR.reason?.message || "Failed to load welfare requests"), []);

      const leaveRequests =
        leaveR.status === "fulfilled"
          ? toArray(leaveR.value.data)
          : ((errors.leave = leaveR.reason?.message || "Failed to load leave requests"), []);

      const machines =
        !isEngineer && machinesR?.status === "fulfilled"
          ? toArray(machinesR.value.data)
          : (!isEngineer &&
              (errors.machines = machinesR?.reason?.message || "Failed to load machines"),
            []);

      const firstVendorId = vendors[0]?.id ?? vendors[0]?.vendorId ?? null;
      const firstMachineId = !isEngineer ? machines[0]?.id ?? machines[0]?.machineId ?? null : null;

      const next = {
        loading: false,
        materialLoading: firstVendorId != null,
        machineDataLoading: firstMachineId != null,
        errors,
        vendors,
        selectedVendorId: firstVendorId,
        dailyMaterial: [],
        stockTransfers,
        advanceRequests,
        welfareRequests,
        leaveRequests,
        machines,
        selectedMachineId: firstMachineId,
        machineFuelRecords: [],
        machineInsuranceRecords: [],
        machineEmiRecords: [],
        machineMaintenanceRecords: [],
      };
      memoryCache = next;
      setState(next);

      // Render everything above immediately; fetch the vendor's material
      // and (admin-only) the machine's summary data as separate,
      // non-blocking steps instead of making the whole page wait on these
      // extra round trips.
      if (firstVendorId != null) {
        loadMaterial(firstVendorId, signal);
      }
      if (!isEngineer && firstMachineId != null) {
        loadMachineData(firstMachineId, signal);
      }
    },
    [loadMaterial, loadMachineData, isEngineer, engineerId]
  );

  useEffect(() => {
    const controller = new AbortController();
    controllerRef.current = controller;
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const reload = useCallback(() => {
    controllerRef.current?.abort();
    memoryCache = null;
    const controller = new AbortController();
    controllerRef.current = controller;
    load(controller.signal);
  }, [load]);

  // Called when the user clicks a machine chip. Cancels any in-flight
  // machine fetch for the previously selected machine before starting
  // the new one, so a slow response for machine A can never overwrite
  // the panel after the user has already switched to machine B.
  // Admin-only — the UI never renders a machine chip for engineers.
  const selectMachine = useCallback(
    (machineId) => {
      setState((s) => ({ ...s, selectedMachineId: machineId }));
      machineControllerRef.current?.abort();
      const controller = new AbortController();
      machineControllerRef.current = controller;
      loadMachineData(machineId, controller.signal);
    },
    [loadMachineData]
  );

  return { ...state, reload, selectMachine, isEngineer };
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------
function PanelHeader({ icon, title, subtitle, onRefresh, busy }) {
  return (
    <div className="ops-panel__header">
      <div className="ops-panel__title-group">
        <span className="ops-panel__icon">{icon}</span>
        <div>
          <h3>{title}</h3>
          {subtitle && <p>{subtitle}</p>}
        </div>
      </div>
      {onRefresh && (
        <button className="ops-panel__refresh" onClick={onRefresh} aria-label="Refresh">
          <RefreshCcw size={15} className={busy ? "ops-spin" : ""} />
        </button>
      )}
    </div>
  );
}

function StatPill({ label, value, tone = "default", onClick }) {
  const clickable = typeof onClick === "function";
  return (
    <div
      className={`ops-stat-pill ops-stat-pill--${tone} ${clickable ? "ops-stat-pill--clickable" : ""}`}
      onClick={onClick}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") onClick();
            }
          : undefined
      }
    >
      <span className="ops-stat-pill__value">{value}</span>
      <span className="ops-stat-pill__label">{label}</span>
    </div>
  );
}

function RequestRow({ request, tone, onClick }) {
  const amount = request.amount ?? request.requestedAmount;
  const name = request.engineerName || request.requestedBy || "Unnamed";
  return (
    <li className="ops-request-row" onClick={onClick} role="button" tabIndex={0}>
      <span className={`ops-request-row__dot ops-request-row__dot--${tone}`} />
      <div className="ops-request-row__main">
        <span className="ops-request-row__name">{name}</span>
        <span className="ops-request-row__meta">
          {formatCurrency(amount)} · {formatDate(request._date)}
          {request._type === "leave" ? " · Leave" : ""}
        </span>
      </div>
      <ChevronRight size={16} className="ops-request-row__chevron" />
    </li>
  );
}

// Status badge used only in the engineer's own-requests view, where we
// show every request regardless of status instead of splitting today/pending.
function StatusBadge({ status }) {
  const s = (status || "pending").toLowerCase();
  const icon =
    s === "approved" ? <CheckCircle2 size={13} /> : s === "rejected" ? <XCircle size={13} /> : <Clock3 size={13} />;
  const tone = STATUS_COLORS[s] || STATUS_COLORS.pending;
  return (
    <span className="ops-status-badge" style={{ color: tone, borderColor: tone }}>
      {icon} {s.charAt(0).toUpperCase() + s.slice(1)}
    </span>
  );
}

function EngineerRequestRow({ request, onClick }) {
  const amount = request.amount ?? request.requestedAmount;
  const typeLabel =
    request._type === "leave" ? "Leave" : request._type === "welfare" ? "Welfare" : "Advance";
  return (
    <li className="ops-request-row" onClick={onClick} role="button" tabIndex={0}>
      <StatusBadge status={request._status} />
      <div className="ops-request-row__main">
        <span className="ops-request-row__name">{typeLabel}</span>
        <span className="ops-request-row__meta">
          {formatCurrency(amount)} · {formatDate(request._date)}
        </span>
      </div>
      <ChevronRight size={16} className="ops-request-row__chevron" />
    </li>
  );
}

// Admin view: today/pending review-queue split across advance/welfare/leave.
function RequestsPanel({ title, icon, advance, welfare, leave, error }) {
  const navigate = useNavigate();

  const advanceSplit = useMemo(() => classifyRequests(advance, "advance"), [advance]);
  const welfareSplit = useMemo(() => classifyRequests(welfare, "welfare"), [welfare]);
  const leaveSplit = useMemo(() => classifyRequests(leave, "leave"), [leave]);

  const todayCount =
    advanceSplit.today.length + welfareSplit.today.length + leaveSplit.today.length;
  const pendingCount =
    advanceSplit.pending.length + welfareSplit.pending.length + leaveSplit.pending.length;

  const chartData = [
    {
      name: "Today",
      Advance: advanceSplit.today.length,
      Welfare: welfareSplit.today.length,
      Leave: leaveSplit.today.length,
    },
    {
      name: "Pending review",
      Advance: advanceSplit.pending.length,
      Welfare: welfareSplit.pending.length,
      Leave: leaveSplit.pending.length,
    },
  ];

  const combinedToday = [...advanceSplit.today, ...welfareSplit.today, ...leaveSplit.today].slice(
    0,
    6
  );
  const combinedPending = [
    ...advanceSplit.pending,
    ...welfareSplit.pending,
    ...leaveSplit.pending,
  ].slice(0, 6);

  const handleRequestClick = useCallback(
    (request) => {
      const buildRoute = ROUTES[request._type];
      if (buildRoute) navigate(buildRoute());
    },
    [navigate]
  );

  return (
    <section className="ops-panel">
      <PanelHeader icon={icon} title={title} subtitle="Engineer review queue" />
      {error && <p className="ops-panel-error">Couldn't fully load: {error}</p>}

      <div className="ops-stat-row">
        <StatPill label="Received today" value={todayCount} tone="today" />
        <StatPill label="Awaiting review" value={pendingCount} tone="pending" />
        <StatPill label="Advance requests" value={advance.length} />
        <StatPill label="Welfare requests" value={welfare.length} />
        <StatPill label="Leave requests" value={leave.length} />
      </div>

      <div className="ops-panel__chart">
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={chartData} barGap={6}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef0f3" />
            <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#6b7280" }} />
            <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#6b7280" }} />
            <Tooltip />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="Advance" fill={STATUS_COLORS.today} radius={[4, 4, 0, 0]} />
            <Bar dataKey="Welfare" fill={STATUS_COLORS.pending} radius={[4, 4, 0, 0]} />
            <Bar dataKey="Leave" fill="#9333ea" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="ops-panel__lists">
        <div className="ops-panel__list-col">
          <h4>
            <Clock3 size={14} /> Today
          </h4>
          {combinedToday.length === 0 ? (
            <p className="ops-empty">No new requests today.</p>
          ) : (
            <ul>
              {combinedToday.map((r, i) => (
                <RequestRow
                  key={`${r._type}-${r.id ?? i}`}
                  request={r}
                  tone="today"
                  onClick={() => handleRequestClick(r)}
                />
              ))}
            </ul>
          )}
        </div>
        <div className="ops-panel__list-col">
          <h4>
            <AlertCircle size={14} /> Pending review
          </h4>
          {combinedPending.length === 0 ? (
            <p className="ops-empty">Nothing overdue for review.</p>
          ) : (
            <ul>
              {combinedPending.map((r, i) => (
                <RequestRow
                  key={`${r._type}-${r.id ?? i}`}
                  request={r}
                  tone="pending"
                  onClick={() => handleRequestClick(r)}
                />
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}

// Engineer's own view: every one of THEIR OWN advance/welfare/leave requests
// (already scoped server-side by engineer id), shown together with its
// actual status — no today/pending review-queue split, since that framing
// only makes sense for whoever is doing the reviewing.
function EngineerRequestsPanel({ title, icon, advance, welfare, leave, error }) {
  const navigate = useNavigate();

  const allRequests = useMemo(() => {
    const combined = [
      ...tagAllRequests(advance, "advance"),
      ...tagAllRequests(welfare, "welfare"),
      ...tagAllRequests(leave, "leave"),
    ];
    // Newest first when a date is available.
    return combined.sort((a, b) => (b._date?.getTime() ?? 0) - (a._date?.getTime() ?? 0));
  }, [advance, welfare, leave]);

  const approvedCount = allRequests.filter((r) => r._status === "approved").length;
  const rejectedCount = allRequests.filter((r) => r._status === "rejected").length;
  const pendingCount = allRequests.filter((r) => r._status === "pending").length;

  const handleRequestClick = useCallback(
    (request) => {
      const buildRoute = ROUTES[request._type];
      if (buildRoute) navigate(buildRoute());
    },
    [navigate]
  );

  return (
    <section className="ops-panel">
      <PanelHeader icon={icon} title={title} subtitle="Your requests — all statuses" />
      {error && <p className="ops-panel-error">Couldn't fully load: {error}</p>}

      <div className="ops-stat-row">
        <StatPill label="Total requests" value={allRequests.length} />
        <StatPill label="Approved" value={approvedCount} tone="today" />
        <StatPill label="Pending" value={pendingCount} tone="pending" />
        <StatPill label="Rejected" value={rejectedCount} />
      </div>

      <div className="ops-panel__lists ops-panel__lists--single">
        <div className="ops-panel__list-col">
          {allRequests.length === 0 ? (
            <p className="ops-empty">You haven't submitted any requests yet.</p>
          ) : (
            <ul>
              {allRequests.map((r, i) => (
                <EngineerRequestRow
                  key={`${r._type}-${r.id ?? i}`}
                  request={r}
                  onClick={() => handleRequestClick(r)}
                />
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}

function MaterialPanel({
  vendors,
  selectedVendorId,
  dailyMaterial,
  stockTransfers,
  error,
  materialLoading,
}) {
  const navigate = useNavigate();

  const materialByType = useMemo(() => {
    const totals = {};
    (Array.isArray(dailyMaterial) ? dailyMaterial : []).forEach((row) => {
      const key = row.materialName || row.material || "Other";
      const qty = Number(row.quantity ?? row.qty ?? 0);
      totals[key] = (totals[key] || 0) + qty;
    });
    return Object.entries(totals).map(([name, value]) => ({ name, value }));
  }, [dailyMaterial]);

  const transfersInProgress = useMemo(
    () => stockTransfers.filter((t) => (t.status || "").toLowerCase() !== "completed").length,
    [stockTransfers]
  );

  return (
    <section className="ops-panel">
      <PanelHeader
        icon={<Package size={18} />}
        title="Material Overview"
        subtitle="Vendor-wise daily material & stock movement"
      />
      {error && <p className="ops-panel-error">Couldn't fully load: {error}</p>}

      <div className="ops-stat-row">
        <StatPill label="Vendors" value={vendors.length} />
        <StatPill label="Stock transfers" value={stockTransfers.length} />
        <StatPill label="In transit" value={transfersInProgress} tone="pending" />
      </div>

      <div className="ops-material-body">
        <div className="ops-vendor-list">
          <h4>
            <Truck size={14} /> Vendors
          </h4>
          <ul>
            {vendors.slice(0, 8).map((v) => {
              const id = v.id ?? v.vendorId;
              const active = id === selectedVendorId;
              return (
                <li key={id}>
                  <button
                    className={`ops-vendor-chip ${active ? "ops-vendor-chip--active" : ""}`}
                    onClick={() => navigate(ROUTES.vendor(id))}
                  >
                    {v.name || v.vendorName || `Vendor ${id}`}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="ops-panel__chart ops-panel__chart--pie">
          {materialLoading ? (
            <p className="ops-empty">Loading material data…</p>
          ) : materialByType.length === 0 ? (
            <p className="ops-empty">No material data for this vendor yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={materialByType}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={45}
                  outerRadius={80}
                  paddingAngle={2}
                >
                  {materialByType.map((entry, i) => (
                    <Cell key={entry.name} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="ops-panel__footer-link">
        <Warehouse size={14} /> Click a vendor to open its material page. Chart shows the first
        vendor by default.
      </div>
    </section>
  );
}

// Machine summary panel: pick a machine from the chip list, then show
// counts pulled from the four machine-id-scoped endpoints — fuel records,
// active insurance, pending EMI, and pending service. Deliberately kept to
// counts only (no charts/tables here); the full breakdown lives on the
// ViewMachine detail page linked at the bottom of the panel.
// Admin-only: Home never renders this for the Engineer role.
function MachinePanel({
  machines,
  selectedMachineId,
  fuelRecords,
  insuranceRecords,
  emiRecords,
  maintenanceRecords,
  error,
  loading,
  onSelectMachine,
}) {
  const navigate = useNavigate();

  const activeInsuranceCount = useMemo(
    () =>
      insuranceRecords.filter((ins) => ins.endDate && new Date(ins.endDate) > new Date()).length,
    [insuranceRecords]
  );
  const pendingEmiCount = useMemo(
    () => emiRecords.filter((emi) => !emi.emiClosedDate).length,
    [emiRecords]
  );
  const pendingServiceCount = useMemo(
    () => maintenanceRecords.filter((s) => (s.status || "").toLowerCase() !== "completed").length,
    [maintenanceRecords]
  );

  const selectedMachine = machines.find((m) => (m.id ?? m.machineId) === selectedMachineId);

  return (
    <section className="ops-panel">
      <PanelHeader
        icon={<Settings size={18} />}
        title="Machine Overview"
        subtitle={
          selectedMachine
            ? selectedMachine.machineName || selectedMachine.name || `Machine ${selectedMachineId}`
            : "Select a machine"
        }
      />
      {error && <p className="ops-panel-error">Couldn't fully load: {error}</p>}

      <div className="ops-material-body">
        <div className="ops-vendor-list">
          <h4>
            <Truck size={14} /> Machines
          </h4>
          <ul>
            {machines.slice(0, 8).map((m) => {
              const id = m.id ?? m.machineId;
              const active = id === selectedMachineId;
              return (
                <li key={id}>
                  <button
                    className={`ops-vendor-chip ${active ? "ops-vendor-chip--active" : ""}`}
                    onClick={() => onSelectMachine(id)}
                  >
                    {m.machineName || m.name || `Machine ${id}`}
                  </button>
                </li>
              );
            })}
            {machines.length === 0 && <p className="ops-empty">No machines found.</p>}
          </ul>
        </div>

        <div className="ops-stat-row ops-stat-row--stacked">
          {loading ? (
            <p className="ops-empty">Loading machine data…</p>
          ) : selectedMachineId == null ? (
            <p className="ops-empty">Select a machine to see its summary.</p>
          ) : (
            <>
              <StatPill
                label="Fuel records"
                value={fuelRecords.length}
                onClick={() => navigate(ROUTES.machine(selectedMachineId))}
              />
              <StatPill
                label="Active insurance"
                value={activeInsuranceCount}
                tone={activeInsuranceCount > 0 ? "today" : "default"}
                onClick={() => navigate(ROUTES.machine(selectedMachineId))}
              />
              <StatPill
                label="Pending EMI"
                value={pendingEmiCount}
                tone={pendingEmiCount > 0 ? "pending" : "default"}
                onClick={() => navigate(ROUTES.machine(selectedMachineId))}
              />
              <StatPill
                label="Pending service"
                value={pendingServiceCount}
                tone={pendingServiceCount > 0 ? "pending" : "default"}
                onClick={() => navigate(ROUTES.machine(selectedMachineId))}
              />
            </>
          )}
        </div>
      </div>

      <div
        className="ops-panel__footer-link"
        role="button"
        tabIndex={0}
        style={{ cursor: selectedMachineId != null ? "pointer" : "default" }}
        onClick={() => selectedMachineId != null && navigate(ROUTES.machine(selectedMachineId))}
      >
        <Wrench size={14} />{" "}
        {selectedMachineId != null ? "Open full machine details" : "Pick a machine above"}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------
function ErrorBanner({ errors, onRetry }) {
  const entries = Object.entries(errors).filter(([, v]) => v);
  if (entries.length === 0) return null;
  return (
    <div className="ops-error-banner">
      <AlertCircle size={15} />
      <span>
        {entries.length} source(s) failed to load: {entries.map(([k, v]) => `${k} (${v})`).join(", ")}
      </span>
      <button onClick={onRetry}>Retry</button>
    </div>
  );
}

export default function Home() {
  const {
    loading,
    materialLoading,
    machineDataLoading,
    errors,
    vendors,
    selectedVendorId,
    dailyMaterial,
    stockTransfers,
    advanceRequests,
    welfareRequests,
    leaveRequests,
    machines,
    selectedMachineId,
    machineFuelRecords,
    machineInsuranceRecords,
    machineEmiRecords,
    machineMaintenanceRecords,
    reload,
    selectMachine,
    isEngineer,
  } = useOperationsData();

  // Only the very first load (no cache, nothing to show yet) blocks the
  // whole page. Every subsequent load/refresh renders the last known data
  // immediately and updates panels in place as fresh data arrives.
  if (loading) {
    return (
      <div className="ops-overview ops-overview--loading">
        <RefreshCcw size={18} className="ops-spin" /> Loading operations overview…
      </div>
    );
  }

  const RequestsComponent = isEngineer ? EngineerRequestsPanel : RequestsPanel;

  return (
    <div className="ops-overview">
      <ErrorBanner errors={errors} onRetry={reload} />
      <div className="ops-overview__grid">
        <MaterialPanel
          vendors={vendors}
          selectedVendorId={selectedVendorId}
          dailyMaterial={dailyMaterial}
          stockTransfers={stockTransfers}
          error={errors.vendors || errors.stock || errors.dailyMaterial}
          materialLoading={materialLoading}
        />
        <RequestsComponent
          title={isEngineer ? "My Requests" : "Approval Requests"}
          icon={<ClipboardList size={18} />}
          advance={advanceRequests}
          welfare={welfareRequests}
          leave={leaveRequests}
          error={errors.advance || errors.welfare || errors.leave}
        />
        {!isEngineer && (
          <MachinePanel
            machines={machines}
            selectedMachineId={selectedMachineId}
            fuelRecords={machineFuelRecords}
            insuranceRecords={machineInsuranceRecords}
            emiRecords={machineEmiRecords}
            maintenanceRecords={machineMaintenanceRecords}
            error={
              errors.machines ||
              errors.machineFuel ||
              errors.machineInsurance ||
              errors.machineEmi ||
              errors.machineMaintenance
            }
            loading={machineDataLoading}
            onSelectMachine={selectMachine}
          />
        )}
      </div>
    </div>
  );
}